import { useCallback, useEffect, useMemo } from 'react';
import { useBulkPricingActionsPermissions } from 'src/components/common/BulkActions/BulkPricingActions/useBulkPricingActionsPermissions';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { useMobileDrawerContext } from 'src/contexts/MobileDrawerContext';
import { BulkEditMarketplacePricesDialog } from 'src/dialogs/BulkEdits/BulkEditMarketplacePricesDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  Listing,
  ListingDetails,
  ListingDetailsUpdateInput,
  ListingPricesUpdateInput,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_MARKETPLACE_PRICES_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { BulkEditMarketplacePricesMobileContent } from './BulkEditMarketplacePriceMobileContent';
import { LaunchUpdateMarketplacePrices } from './LaunchChangeMarketplacePrice';

export const useBulkUpdateMarketplacePrices = (
  filterQueryWithEventIds: ListingQuery,
  affectedVisibleListingIds: number[],
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const updateKey = event
    ? `marketplacePrices-event-${event?.virtId}`
    : INVENTORY_BULK_UPDATE_MARKETPLACE_PRICES_UPDATE_KEY;

  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();
  const { openWithContent, close } = useMobileDrawerContext();

  const {
    eventsTransformed,
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const { showErrorDialog } = useClientLogContext();

  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasUpdateListingPermission, hasEditPricingPermission } =
    useBulkPricingActionsPermissions();

  const {
    openDialog: openBulkEditMarketplaceDialog,
    closeDialog: closeBulkEditMarketplaceDialog,
  } = useDialog(
    DialogId.BulkEditMarketplacePrices,
    BulkEditMarketplacePricesDialog
  );

  const onSubmit = useCallback(
    async (
      listingUpdate: ListingDetailsUpdateInput | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateMarketplacePricesPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateMarketplacePricesPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (listingUpdate) {
        await tryInvokeApi(
          async () => {
            const client = await new BulkEditListingClient(
              activeAccountWebClientConfig
            );

            const input = {
              item1: preview!.preview,
              item2: {
                id: listingUpdate.id,
                allInPrice: listingUpdate.allInPrice,
                listPrice: listingUpdate.listPrice,
                netProceedsCeiling: listingUpdate.netProceedsCeiling,
                netProceedsFloor: listingUpdate.netProceedsFloor,
                autoPricingEnabled: listingUpdate.autoPricingEnabled,
                marketplacePriceUpdates: listingUpdate.marketplacePriceUpdates,
              } as ListingPricesUpdateInput,
            };
            const succeeded = await client.bulkUpdateMarketplacePrices(
              input,
              updateKey,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkEditMarketplaceDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateMarketplacePrices', error, {
              trackErrorData: { preview, listingUpdate },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeBulkEditMarketplaceDialog,
      filterQueryWithEventIds,
      refreshExpandedListItems,
      setActivePosEntity,
      setIsLoading,
      showErrorDialog,
      updateKey,
    ]
  );

  const listings = useMemo(() => {
    return (eventsTransformed ?? [])
      .flatMap((ev) => ev.entities.listings)
      .flatMap(flattenListingGroup)
      .filter(({ id, isLtGrp }) => id > 0 && !isLtGrp) as Listing[];
  }, [eventsTransformed]);

  const openDialog = useEventCallback(() => {
    openBulkEditMarketplaceDialog({
      event,
      updateKey,
      listings,
      selectedListingIds: affectedVisibleListingIds,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditMarketplaceDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditUpdateMarketplacePrices,
        onSelect: openDialog,
      },
      disabled: !hasUpdateListingPermission || !hasEditPricingPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditUpdateMarketplacePrices,
        disabled: !hasUpdateListingPermission || !hasEditPricingPermission,
      });
    };
  }, [
    hasEditPricingPermission,
    hasUpdateListingPermission,
    openDialog,
    registerCommand,
    unregisterCommand,
  ]);

  // For mobile use
  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasEditPricingPermission || !hasUpdateListingPermission) {
      return [];
    }
    return [
      {
        title: ContentId.UpdateMarketplacePrices,
        disabled: isLoading,
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
          openWithContent(
            <BulkEditMarketplacePricesMobileContent
              event={event}
              updateKey={updateKey}
              onOkay={onSubmit}
              onCancel={close}
            />,
            {
              size: '100%',
              zIndex: 1000,
              title: <Content id={ContentId.UpdateMarketplacePrices} />,
            }
          );
        },
      },
    ];
  }, [
    close,
    event,
    hasEditPricingPermission,
    hasUpdateListingPermission,
    isLoading,
    onSubmit,
    openWithContent,
    updateKey,
  ]);

  return {
    dropDown: (
      <LaunchUpdateMarketplacePrices
        key={'LaunchUpdateMarketplacePrices'}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openBulkEditMarketplaceDialog({
            event,
            updateKey,
            listings,
            selectedListingIds: affectedVisibleListingIds,
            onOkay: onSubmit,
            onClosed: () => {
              setIsLoading(false);
            },
            onCancel: closeBulkEditMarketplaceDialog,
          });
        }}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
